import { loadOpenComponentsClient } from '@chegg/wtf-components'
import { getLoggedIn, getSessionId } from '@src/state/auth/selectors'
import { EVENT_LABELS } from '@src/state/gtm/definitions/constants'
import {
  hideModal as hideModalAction,
  PAPER_UPLOADED_NON_PLUS_MODAL,
  PAYMENT_MODAL,
  showModal,
} from '@src/state/modals/actions'
import { transferPapers } from '@src/state/papers/actions'
import { getSite } from '@src/state/site/selectors'
import { RootState } from '@src/state/types'
import {
  AuthEventData,
  AuthSuccessGrant,
  loadCheggAuthComponent,
} from '@src/utils/cheggAuth'
import { Cookies, removeCookie } from '@src/utils/cookies'
import { captureError } from '@src/utils/errors'
import { tokenRequest } from '@src/utils/http-clients/cfeAuthApi'
import { deleteCheggSession } from '@src/utils/http-clients/cheggSessionApi'
import { parseJwt } from '@src/utils/jwt'
import {
  clearJwt,
  clearJwtLegacyCookie,
  getCheggSessionId,
  setJwt,
} from '@src/utils/localStorage'
import { push } from 'connected-react-router'
import { Action, createLogic } from 'redux-logic'
import { getType } from 'typesafe-actions'
import { resetState } from '../actions'
import { getRioClient } from '../analytics/rioAnalytics'
import { clearLocalStorage } from '../session/actions'
import { CHEGG } from '../site/types'
import {
  authFlowComplete,
  AuthPayload,
  cfeLogin,
  cheggAuthLoaded,
  FreshToken,
  handleCorruptToken,
  handleFreshToken,
  hideCheggAuthModal,
  initToken,
  loadCheggAuth,
  LoggedInStateBeforeCorruption,
  logout,
  setParsedJwt,
  setSelectedUserSchoolType,
  setSelectedUserSignUpType,
  setSuccessRedirect,
  ShowCheggAuthExperience,
  showCheggAuthModal,
  startAuthFlow,
  viewAuthSignInMode,
  viewAuthSignUpMode,
} from './actions'
import { WritingToolsJWT } from './types'
import { logout as OneAuthLogout } from '@chegg/one-auth-sdk'
import { getCheggAuthRedirectUrl } from '@src/state/site/selectors'

export const getShouldShowPaymentModal = (
  authType: string,
  freeUser: boolean,
  showPayWallOnLogin: boolean
): boolean => {
  return authType === 'signup' || (freeUser && showPayWallOnLogin)
}

export const initTokenLogic = createLogic<RootState>({
  type: [getType(initToken)],
  debounce: 500,
  process({}, _, done) {
    // if token lives in local storage, we always have to refresh on init
    tokenRequest()
    clearJwtLegacyCookie()
    done()
  },
})

export const handleFreshTokenLogic = createLogic<RootState, FreshToken>({
  type: getType(handleFreshToken),
  validate({ getState, action }, allow, reject) {
    const wasLoggedIn = getLoggedIn(getState())
    try {
      const { cheggTokens, paidMember } = parseJwt(action.payload.jwt)

      if (!cheggTokens) {
        captureError(new Error('JWT with no cheggTokens received'))
        return reject(handleCorruptToken({ wasLoggedIn: true }))
      }
      const { access_token, id_token } = cheggTokens
      // There is a bug where CFE returns a JWT with paidMember === true and no access_token.
      // This is a patch to handle this more elegantly and gain more metrics on the frequency
      if (paidMember && !access_token) {
        captureError(new Error('JWT with paidMember and no access token received'))
        return reject(handleCorruptToken({ wasLoggedIn: true }))
      }

      if (getSite(getState()) === CHEGG && id_token && !access_token) {
        return reject(handleCorruptToken({ wasLoggedIn: true }))
      }

      allow(action)
    } catch (error) {
      reject(handleCorruptToken({ wasLoggedIn }))
    }
  },
  process({ getState, action }, dispatch, done) {
    const { jwt } = action.payload
    const state = getState()
    const oldSessionId = getSessionId(state)
    const parsedJwt = parseJwt(jwt)
    setJwt(jwt)
    dispatch(
      setParsedJwt({
        ...(parsedJwt as WritingToolsJWT),
        isLogin: !!action.payload.loginAuthType,
      })
    )
    if (
      action.payload.loginAuthType &&
      action.payload.loginAuthType !== 'refresh_token' &&
      oldSessionId
    ) {
      dispatch(transferPapers({ oldSessionId }))
    }
    done()
  },
})

export const handleCorruptTokenLogic = createLogic<
  RootState,
  LoggedInStateBeforeCorruption
>({
  type: getType(handleCorruptToken),
  process({ action }, dispatch, done) {
    const { wasLoggedIn } = action.payload as LoggedInStateBeforeCorruption
    if (wasLoggedIn === true) {
      dispatch(showCheggAuthModal({ experience: 'login' }) as Action)
    }
    dispatch(logout({}))
    done()
  },
})

export const logoutLogic = createLogic<RootState>({
  type: getType(logout),
  async process({ getState }, dispatch, done) {
    const state = getState()

    // get value before CFE deletes cookies
    const cheggSessionId = getCheggSessionId()
    sessionStorage.removeItem('createPaperSuccess')
    try {
      await OneAuthLogout(true)
    } catch (error) {
      captureError(error)
    }

    dispatch(resetState())
    clearJwt()
    removeCookie(Cookies.PaperUploaded, state.site)

    if (state.site === CHEGG) {
      if (cheggSessionId) {
        await deleteCheggSession()
      }
      if (window.globalChggHeader) {
        window.globalChggHeader.trigger('refresh_auth_status')
      }
    }

    try {
      await tokenRequest({
        fromLogout: true,
      })

      dispatch(clearLocalStorage())

      dispatch(push(`/papers`))
    } catch (e) {
      dispatch(push(`/error`))
    }

    done()
    window.location.reload()
  },
})

export const cfeLoginLogic = createLogic<RootState, AuthSuccessGrant>({
  type: getType(cfeLogin),
  async process({ getState, action }, dispatch, done) {
    const cheggAuthGrantObject = action.payload as AuthSuccessGrant
    const state = getState()

    const { router } = state
    const { showPaywallOnLogin } = state.payment
    const { cspUpsell } = state.featureFlags
    const { successRedirect } = state.auth || cheggAuthGrantObject?.redirect_uri
    const { shouldSubmitAfterLogin } = state.expertCheck

    try {
      const jwt = await tokenRequest({
        fromLogin: true,
      })

      let { auth_type } = cheggAuthGrantObject
      dispatch(
        handleFreshToken({
          jwt,
          loginAuthType: auth_type,
        })
      )
      const parsedJwt = parseJwt(jwt)

      getRioClient().updateState({
        cheggUuid: parsedJwt.cheggUserUuid as string,
      })

      const freeUser = !parsedJwt.paidMember
      // auth0 does not return auth_type in payload, hence manually setting it to login for paid users
      if (!freeUser) {
        auth_type = 'login'
      }
      const shouldShowPM = getShouldShowPaymentModal(
        auth_type,
        freeUser,
        showPaywallOnLogin
      )

      if (!shouldSubmitAfterLogin && shouldShowPM) {
        if (cspUpsell?.cspUpsellShowInterstitial) {
          dispatch(push('/plans', { redirectPath: router.location.pathname }))
        } else {
          dispatch(
            showModal({
              name: PAYMENT_MODAL,
            })
          )
        }
      }
      dispatch(authFlowComplete())
      if (state.site === CHEGG) {
        if (window.globalChggHeader) {
          window.globalChggHeader.trigger('refresh_auth_status')
        }
      }

      // For context, see `src/state/auth/types.ts`, and note the tsdoc
      // comment on `successRedirect`.
      if (successRedirect) {
        dispatch(push(successRedirect))

        dispatch(
          setSuccessRedirect({
            successRedirect: null,
          })
        )
      }
    } catch (error) {
      dispatch(handleCorruptToken({ wasLoggedIn: true }))
    }
    done()
  },
})

export const loadCheggAuthLogic = createLogic<RootState>({
  type: getType(loadCheggAuth),
  warnTimeout: 0, // ensure that `dispatch` calls
  // in callbacks do not expire or error

  async process({ getState }, dispatch, done) {
    const successCallback = (saOptions: AuthSuccessGrant) => {
      dispatch(cfeLogin(saOptions))
    }

    const authEventCallback = (event: Event, data: AuthEventData) => {
      const { eventInfo } = data
      if (eventInfo) {
        const {
          signup_viewed,
          signin_viewed,
          signup_user_type_selected,
          signup_student_type_selected,
          signup_teacher_teaching_in_selected,
        } = eventInfo

        const schoolType =
          signup_teacher_teaching_in_selected || signup_student_type_selected

        if (signin_viewed) {
          dispatch(viewAuthSignInMode())
          return
        }

        if (signup_viewed) {
          dispatch(viewAuthSignUpMode())
          return
        }

        if (signup_user_type_selected) {
          dispatch(setSelectedUserSignUpType({ memberRole: signup_user_type_selected }))
          return
        }

        if (schoolType) {
          dispatch(setSelectedUserSchoolType({ schoolType }))
          return
        }
      }
    }

    const hideModal = () => dispatch(hideCheggAuthModal())

    const onReadyCb = () => dispatch(cheggAuthLoaded())

    try {
      loadCheggAuthComponent(onReadyCb, successCallback, hideModal, authEventCallback)

      loadOpenComponentsClient()
    } catch (error) {
      // TODOs ->
      //  1) show user some sort of error msg?
      captureError(error)
      console.error(`unhandled error from 'loadCheggAuthLogic': ${error}`)

      // Only fire `done()` in the error handler
      //  as done() will disable any subsequent calls to `dispatch`
      //  We want the dispatch references passed to the callbacks to stay valid
      done()
    }
  },
})

export const showAuthModal = createLogic<RootState, ShowCheggAuthExperience>({
  type: getType(showCheggAuthModal),
  process({ action }, dispatch, done) {
    const { experience, origin } = action.payload as ShowCheggAuthExperience
    if (origin === PAPER_UPLOADED_NON_PLUS_MODAL) {
      dispatch(
        hideModalAction(
          { name: PAPER_UPLOADED_NON_PLUS_MODAL },
          { origin: EVENT_LABELS.UPSELL_MODAL_LINK }
        )
      )
    }

    dispatch(
      startAuthFlow({ view: experience === 'login' ? 'signin' : 'signup', origin })
    )

    done()
  },
})

export const authFlowCompleteLogic = createLogic<RootState>({
  type: getType(authFlowComplete),
  process() {
    window.globalCheggAuth?.trigger('hide')
  },
})

export const startAuthFlowLogic = createLogic<RootState, AuthPayload>({
  type: getType(startAuthFlow),
  process({ getState, action }, _, done) {
    try {
      const { view, origin } = action.payload
      const site = getSite(getState())
      let authURL = getCheggAuthRedirectUrl(site)
      const defaultFlow = view || 'login'
      if (defaultFlow === 'signup') {
        authURL += '&action=signup'
      }
      const hasQueryParams = window.location.href.includes('?')
      // Form the redirect URL based on whether query parameters exist
      const redirect = hasQueryParams
        ? `${window.location.href}&authSuccess=true&auth_type=${defaultFlow}&origin=${origin}`
        : `${window.location.href}?authSuccess=true&auth_type=${defaultFlow}&origin=${origin}`

      const finalURL = authURL + '&redirect=' + encodeURIComponent(redirect)

      window.location.href = finalURL
    } catch (error) {
      console.error(`Failed to redirect to login. Please try again.`, error)
    } finally {
      done()
    }
  },
})

export default [
  authFlowCompleteLogic,
  initTokenLogic,
  handleFreshTokenLogic,
  handleCorruptTokenLogic,
  logoutLogic,
  cfeLoginLogic,
  showAuthModal,
  startAuthFlowLogic,
]



// WEBPACK FOOTER //
// ./src/state/auth/logic.ts